/**
 * Whoami diagnostic — dumps the current user's identity, raw JWT roles,
 * computed useClientAccess flags, and effective UI-visibility settings.
 *
 * Not linked in the sidebar. Open directly at /whoami. Also writes the
 * full payload to console.info on mount so QA can verify what's
 * reaching the client without copy-pasting from the DOM.
 *
 * Visible to any authenticated user — it shows the caller their own
 * identity only, no admin data, no other users' info. Safe to expose.
 */

import React, { useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useClientAccess } from '../hooks/useClientAccess';
import { useUiVisibility } from '../contexts/UiVisibilityContext';
import { safeStorage } from '../utils/safeStorage';
import config from '../config/env';

function decodeJwtSafe(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const pad = parts[1] + '='.repeat((-parts[1].length) & 3);
    return JSON.parse(atob(pad.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}

export const WhoAmIPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const access = useClientAccess();
  const visibility = useUiVisibility();

  const rawJwtClaims = useMemo(() => {
    const tok = safeStorage.getItem('access_token');
    return decodeJwtSafe(tok);
  }, []);

  // Whitelist the access shape we care about; useClientAccess returns
  // memoized callbacks too which would serialise as "[Function]".
  const accessFlags = useMemo(() => ({
    isSuperAdmin: access.isSuperAdmin,
    isAdmin: access.isAdmin,
    isClientAdmin: access.isClientAdmin,
    isOperator: access.isOperator,
    // `isViewer` isn't exposed by the hook (only the derived
    // `isViewerOnly`), so check the raw role list directly.
    isViewer: (user?.roles ?? []).includes('Viewer'),
    isViewerOnly: access.isViewerOnly,
    canManageUsers: access.canManageUsers,
    canEditDeviceTypes: access.canEditDeviceTypes,
    canViewAuditLog: access.canViewAuditLog,
    hasAnyClientAccess: access.hasAnyClientAccess,
    hasFullAccess: access.hasFullAccess,
    hasSingleClient: access.hasSingleClient,
    shouldShowClientUI: access.shouldShowClientUI,
    allowedClientIds: access.allowedClientIds,
  }), [access, user]);

  useEffect(() => {
    // Single grouped console entry so it's easy to spot.
    /* eslint-disable no-console */
    console.groupCollapsed(`[whoami] ${user?.preferredUsername || '(anon)'} — roles + scope`);
    console.info('isAuthenticated:', isAuthenticated);
    console.info('user:', user);
    console.info('useClientAccess:', accessFlags);
    console.info('useUiVisibility:', {
      showApiStatus: visibility.showApiStatus,
      showDebugMenu: visibility.showDebugMenu,
      showLanguageSelector: visibility.showLanguageSelector,
      debugMenuUsernames: visibility.debugMenuUsernames,
      showRawTab: visibility.showRawTab,
      rawTabUsernames: visibility.rawTabUsernames,
      showJsonConfig: visibility.showJsonConfig,
      jsonConfigUsernames: visibility.jsonConfigUsernames,
      ready: visibility.ready,
    });
    console.info('raw JWT claims:', rawJwtClaims);
    console.groupEnd();
    /* eslint-enable no-console */
  }, [isAuthenticated, user, accessFlags, visibility, rawJwtClaims]);

  // Defense-in-depth: the App.tsx route registration is already gated
  // on config.dev.debug, but if anything else mounts this component
  // (test harness, story, etc.) on a non-debug build, refuse to render.
  if (!config.dev.debug) {
    return null;
  }

  if (!isAuthenticated) {
    return (
      <div className="page whoami-page">
        <h1>Whoami</h1>
        <p>Not authenticated.</p>
      </div>
    );
  }

  const k = (v: unknown) =>
    typeof v === 'boolean' ? (v ? '✓' : '✗') : v === null || v === undefined ? '—' : String(v);
  const Row = ({ label, value }: { label: string; value: unknown }) => (
    <tr>
      <th style={{ textAlign: 'left', padding: '4px 12px 4px 0', fontWeight: 500 }}>{label}</th>
      <td style={{ padding: '4px 0', fontFamily: 'ui-monospace, monospace', fontSize: '0.875rem' }}>
        {Array.isArray(value)
          ? value.length === 0 ? <em>—</em> : value.map(String).join(', ')
          : typeof value === 'object' && value !== null
            ? <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>
            : k(value)}
      </td>
    </tr>
  );

  return (
    <div className="page whoami-page" data-testid="whoami-page" style={{ padding: '1.5rem', maxWidth: 900 }}>
      <h1 style={{ marginTop: 0 }}>Whoami</h1>
      <p style={{ color: 'var(--theme-text-secondary, #6b7280)', marginTop: 0 }}>
        Diagnostic view of the current session. Everything below is also
        printed to the browser console under a single grouped log entry.
      </p>

      <h2 style={{ fontSize: '1.1rem' }}>Identity</h2>
      <table>
        <tbody>
          <Row label="preferred_username" value={user?.preferredUsername} />
          <Row label="name"                value={user?.name} />
          <Row label="email"               value={user?.email} />
          <Row label="id (sub)"            value={user?.id} />
          <Row label="roles (JWT)"         value={user?.roles ?? []} />
          <Row label="clientIds (from /Clients/* groups)" value={user?.clientIds ?? []} />
        </tbody>
      </table>

      <h2 style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>useClientAccess flags</h2>
      <table>
        <tbody>
          {(Object.keys(accessFlags) as Array<keyof typeof accessFlags>).map(key => (
            <Row key={key} label={key} value={accessFlags[key]} />
          ))}
        </tbody>
      </table>

      <h2 style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>useUiVisibility (effective)</h2>
      <table>
        <tbody>
          <Row label="showApiStatus"        value={visibility.showApiStatus} />
          <Row label="showDebugMenu"        value={visibility.showDebugMenu} />
          <Row label="showLanguageSelector" value={visibility.showLanguageSelector} />
          <Row label="debugMenuUsernames"   value={visibility.debugMenuUsernames} />
          <Row label="showRawTab"           value={visibility.showRawTab} />
          <Row label="rawTabUsernames"      value={visibility.rawTabUsernames} />
          <Row label="showJsonConfig"       value={visibility.showJsonConfig} />
          <Row label="jsonConfigUsernames"  value={visibility.jsonConfigUsernames} />
          <Row label="ready"                value={visibility.ready} />
        </tbody>
      </table>

      <h2 style={{ fontSize: '1.1rem', marginTop: '1.5rem' }}>Raw JWT claims</h2>
      <pre style={{ fontSize: '0.78rem', overflow: 'auto', padding: '0.75rem', background: 'var(--theme-bg-secondary, #f3f4f6)', borderRadius: 6 }}>
        {JSON.stringify(rawJwtClaims, null, 2)}
      </pre>
    </div>
  );
};

export default WhoAmIPage;
